package com.arena_web.ui.sections.chat.mobile

import androidx.compose.runtime.*
import com.arena_web.data.dto.FollowUserDto
import com.arena_web.ui.components.cards.PlayerCard
import com.arena_web.ui.components.cards.PlayerCardSize
import com.arena_web.ui.components.chat.NewChatTab
import com.arena_web.ui.components.overlay.BottomSheet
import com.arena_web.ui.components.widgets.InlineSpinner
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.AppGradients
import com.arena_web.ui.theme.AppIcons
import com.arena_web.ui.theme.bodyLMedium
import com.arena_web.ui.theme.bodyLRegular
import com.arena_web.ui.theme.bodyMRegular
import com.arena_web.ui.theme.bodyXlRegular
import com.arena_web.ui.theme.h3
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.TextInput
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.FileReader
import org.w3c.files.get

/**
 * `Interactive/Secondary` fill over an opaque body so the translucent gradient
 * keeps its tint, plus a `Button/border/gradient/primary` hairline — the recipe
 * the mobile chat screen uses for inputs and the segmented control. Painted as
 * stacked backgrounds so the stroke survives without a separate mask layer.
 */
private val FieldBackground =
    "${AppGradients.InteractiveSecondary} padding-box, " +
        "linear-gradient(0deg, var(--arena-bg), var(--arena-bg)) padding-box, " +
        "${AppGradients.ButtonBorderGradientPrimary} border-box"

/**
 * Mobile "New Chat" bottom sheet — the phone counterpart to [com.arena_web.ui
 * .components.chat.NewChatDialog]. Same callbacks and behaviour (Direct search +
 * Community create), re-laid-out as a bottom sheet per Figma 8421:119263 /
 * 8425:205773. Lives behind the sheet's `--scale: var(--mobile-scale)` wrapper,
 * so the desktop `s()` / typography helpers render at the 390-px mobile baseline.
 */
@Composable
fun MobileNewChatSheet(
    following: List<FollowUserDto>,
    isLoading: Boolean,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    searchQuery: String,
    isCreating: Boolean,
    onSearchQueryChanged: (String) -> Unit,
    onCreateDirect: (targetUserId: String) -> Unit,
    onCreateGroup: (name: String, imageBytes: ByteArray?, imageFileName: String?, imageContentType: String?) -> Unit,
    onLoadMore: () -> Unit,
    onDismiss: () -> Unit,
) {
    val t = LocalAppStrings.current
    var activeTab by remember { mutableStateOf(NewChatTab.DIRECT) }
    var groupName by remember { mutableStateOf("") }
    var imageBytes by remember { mutableStateOf<ByteArray?>(null) }
    var imageFileName by remember { mutableStateOf<String?>(null) }
    var imageContentType by remember { mutableStateOf<String?>(null) }
    var imagePreviewUrl by remember { mutableStateOf<String?>(null) }

    BottomSheet(onDismiss = onDismiss) {
        // Outer content column: header group (handle gap is supplied by the
        // sheet) → tab body, 24-px apart per Figma 8425:205908.
        Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(24)) }) {
            // Title + segmented control, 16 px apart.
            Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(16)) }) {
                SpanText(t.chatNewChatTitle, modifier = Modifier.h3().color(com.arena_web.ui.theme.ThemeVars.Text2))
                SegmentedControl(activeTab = activeTab, onTabChanged = { activeTab = it })
            }

            when (activeTab) {
                NewChatTab.DIRECT -> DirectTab(
                    searchQuery = searchQuery,
                    onSearchChanged = onSearchQueryChanged,
                    results = following,
                    isLoading = isLoading,
                    isLoadingMore = isLoadingMore,
                    hasMore = hasMore,
                    isCreating = isCreating,
                    onCreateDirect = onCreateDirect,
                    onLoadMore = onLoadMore,
                )

                NewChatTab.GROUP -> CommunityTab(
                    groupName = groupName,
                    onGroupNameChanged = { groupName = it },
                    imagePreviewUrl = imagePreviewUrl,
                    onPickImage = {
                        val input = document.createElement("input") as HTMLInputElement
                        input.type = "file"
                        input.accept = "image/*"
                        input.onchange = { _ ->
                            val file = input.files?.get(0)
                            if (file != null) {
                                imagePreviewUrl = URL.createObjectURL(file)
                                imageFileName = file.name
                                imageContentType = file.type
                                val reader = FileReader()
                                reader.onload = {
                                    val arrayBuffer = reader.result.unsafeCast<org.khronos.webgl.ArrayBuffer>()
                                    val int8Array = org.khronos.webgl.Int8Array(buffer = arrayBuffer)
                                    imageBytes = int8Array.unsafeCast<ByteArray>()
                                }
                                reader.readAsArrayBuffer(file)
                            }
                        }
                        input.click()
                    },
                    isCreating = isCreating,
                    onCreateGroup = {
                        onCreateGroup(groupName.trim(), imageBytes, imageFileName, imageContentType)
                    },
                    onCancel = onDismiss,
                )
            }
        }
    }
}

// ── Segmented Control ──

@Composable
private fun SegmentedControl(activeTab: NewChatTab, onTabChanged: (NewChatTab) -> Unit) {
    val t = LocalAppStrings.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("height", s(48))
                property("padding", s(4))
                property("border-radius", s(34))
                property("border", "0.3px solid transparent")
                property("background", FieldBackground)
                property("box-sizing", "border-box")
            },
        verticalAlignment = Alignment.CenterVertically
    ) {
        SegmentTab(t.chatTabDirectMessage, activeTab == NewChatTab.DIRECT) { onTabChanged(NewChatTab.DIRECT) }
        SegmentTab(t.chatTabCommunity, activeTab == NewChatTab.GROUP) { onTabChanged(NewChatTab.GROUP) }
    }
}

@Composable
private fun SegmentTab(label: String, isActive: Boolean, onClick: () -> Unit) {
    Box(
        modifier = Modifier
            .styleModifier {
                property("flex", "1")
                property("height", s(40))
                property("border-radius", s(28))
                if (isActive) property("background", "#0061ff")
            }
            .cursor(Cursor.Pointer)
            .onClick { onClick() },
        contentAlignment = Alignment.Center
    ) {
        SpanText(
            label,
            modifier = Modifier
                .bodyLMedium()
                .color(if (isActive) org.jetbrains.compose.web.css.Color(AppColors.PrimaryWhite.toString()) else com.arena_web.ui.theme.ThemeVars.Text2)
                .whiteSpace(WhiteSpace.NoWrap)
        )
    }
}

// ── Direct Message tab ──

@Composable
private fun DirectTab(
    searchQuery: String,
    onSearchChanged: (String) -> Unit,
    results: List<FollowUserDto>,
    isLoading: Boolean,
    isLoadingMore: Boolean,
    hasMore: Boolean,
    isCreating: Boolean,
    onCreateDirect: (String) -> Unit,
    onLoadMore: () -> Unit,
) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(32)) }) {
        // Search field.
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("height", s(44))
                    property("border-radius", s(28))
                    property("padding", "0 ${s(8)} 0 ${s(12)}")
                    property("gap", s(6))
                    property("border", "0.5px solid transparent")
                    property("background", FieldBackground)
                    property("box-sizing", "border-box")
                },
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .sSize(24)
                    .styleModifier {
                        property("flex-shrink", "0")
                        property("background-color", "#818D9C")
                        property("mask", "url('${AppIcons.Search}') no-repeat center / contain")
                        property("-webkit-mask", "url('${AppIcons.Search}') no-repeat center / contain")
                    }
            )
            TextInput(
                value = searchQuery,
                attrs = Modifier
                    .fillMaxWidth()
                    .bodyLRegular()
                    .color(com.arena_web.ui.theme.ThemeVars.Text1)
                    .styleModifier {
                        property("background", "transparent")
                        property("border", "none")
                        property("outline", "none")
                        property("min-width", "0")
                        property("font-family", "Urbanist, sans-serif")
                    }
                    .toAttrs {
                        attr("placeholder", t.chatSearchFriendsPlaceholder)
                        onInput { e -> onSearchChanged(e.target.asDynamic().value as String) }
                    }
            )
        }

        // Friends list. Scroll fires `onLoadMore` near the bottom (same 80-px
        // threshold as the desktop dialog). Capped so the sheet never grows
        // past the viewport — the panel itself is content-sized.
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier {
                    property("max-height", "52vh")
                    property("overflow-y", "auto")
                    property("scrollbar-width", "none")
                    property("gap", s(24))
                }
                .attrsModifier {
                    onScroll { evt ->
                        if (hasMore && !isLoading && !isLoadingMore) {
                            val el = evt.target.unsafeCast<org.w3c.dom.HTMLElement>()
                            val nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80.0
                            if (nearBottom) onLoadMore()
                        }
                    }
                }
        ) {
            if (isLoading) {
                Box(modifier = Modifier.fillMaxWidth().sPadding(24, 0), contentAlignment = Alignment.Center) {
                    Box {} /* no loading indicator (user pref) */
                }
            } else if (results.isEmpty()) {
                Box(modifier = Modifier.fillMaxWidth().sPadding(24, 0), contentAlignment = Alignment.Center) {
                    SpanText(
                        if (searchQuery.isBlank()) t.chatNotFollowingAnyone else t.chatNoResults,
                        modifier = Modifier.bodyMRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
                    )
                }
            } else {
                results.forEach { user ->
                    FriendRow(user = user, isCreating = isCreating, onClick = { onCreateDirect(user.userId) })
                }
                if (isLoadingMore) {
                    Box(modifier = Modifier.fillMaxWidth().sPadding(8, 0), contentAlignment = Alignment.Center) {
                        Box {} /* no loading indicator (user pref) */
                    }
                }
            }
        }
    }
}

@Composable
private fun FriendRow(user: FollowUserDto, isCreating: Boolean, onClick: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .cursor(if (isCreating) Cursor.Default else Cursor.Pointer)
            .onClick { if (!isCreating) onClick() }
            .styleModifier { property("gap", s(8)) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        val cardFrameUrl = user.cardImageUrl
        if (cardFrameUrl != null) {
            Box(
                modifier = Modifier.styleModifier {
                    property("flex-shrink", "0")
                    property("width", s(40))
                    property("height", s(60))
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                },
                contentAlignment = Alignment.Center
            ) {
                PlayerCard(
                    playerName = "",
                    rating = 0,
                    position = "",
                    sport = "football",
                    size = PlayerCardSize.Highlight,
                    cardFrameUrl = cardFrameUrl,
                    avatarUrl = user.cardAvatarUrl ?: AppIcons.EmptyAvatar,
                    hideRating = true,
                    hideShadow = true,
                )
            }
        } else {
            val avatarUrl = user.avatarUrl
            Box(
                modifier = Modifier
                    .sWidth(40)
                    .sHeight(60)
                    .styleModifier {
                        property("border-radius", s(6))
                        property("background", "rgba(60, 121, 241, 0.15)")
                        property("overflow", "hidden")
                        property("flex-shrink", "0")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (avatarUrl != null) {
                    Image(src = avatarUrl, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
                } else {
                    SpanText(
                        user.username.take(1).uppercase(),
                        modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2)
                    )
                }
            }
        }
        Column(modifier = Modifier.styleModifier { property("gap", s(2)) }) {
            SpanText(
                user.fullName ?: user.username,
                modifier = Modifier
                    .bodyXlRegular()
                    .color(com.arena_web.ui.theme.ThemeVars.Text2)
                    .whiteSpace(WhiteSpace.NoWrap)
                    .overflow(Overflow.Hidden)
                    .textOverflow(TextOverflow.Ellipsis)
            )
            SpanText(
                "@${user.username}",
                modifier = Modifier.bodyMRegular().color(com.arena_web.ui.theme.ThemeVars.Text3)
            )
        }
    }
}

// ── Community tab ──

@Composable
private fun CommunityTab(
    groupName: String,
    onGroupNameChanged: (String) -> Unit,
    imagePreviewUrl: String?,
    onPickImage: () -> Unit,
    isCreating: Boolean,
    onCreateGroup: () -> Unit,
    onCancel: () -> Unit,
) {
    val t = LocalAppStrings.current
    Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(40)) }) {
        Column(
            modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(28)) },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            // Camera avatar / preview — Figma 8425:205248 (80-px circle).
            Box(
                modifier = Modifier
                    .sSize(80)
                    .sBorderRadius(100)
                    .cursor(Cursor.Pointer)
                    .onClick { onPickImage() }
                    .styleModifier {
                        property("background", "rgba(0, 36, 93, 0.8)")
                        property("flex-shrink", "0")
                        property("overflow", "hidden")
                    },
                contentAlignment = Alignment.Center
            ) {
                if (imagePreviewUrl != null) {
                    Image(src = imagePreviewUrl, modifier = Modifier.fillMaxSize().objectFit(ObjectFit.Cover))
                } else {
                    Image(src = AppIcons.Camera, modifier = Modifier.sSize(24))
                }
            }

            // Community name field.
            Column(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(6)) }) {
                SpanText(
                    t.chatCommunityNameLabel,
                    modifier = Modifier
                        .bodyMRegular()
                        .color(Color("#818d9c"))
                        .styleModifier { property("padding-left", s(4)) }
                )
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("height", s(44))
                            property("border-radius", s(28))
                            property("padding", "0 ${s(8)} 0 ${s(12)}")
                            property("gap", s(12))
                            property("border", "0.5px solid transparent")
                            property("background", FieldBackground)
                            property("box-sizing", "border-box")
                        },
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    TextInput(
                        value = groupName,
                        attrs = Modifier
                            .fillMaxWidth()
                            .bodyLRegular()
                            .color(com.arena_web.ui.theme.ThemeVars.Text1)
                            .styleModifier {
                                property("background", "transparent")
                                property("border", "none")
                                property("outline", "none")
                                property("min-width", "0")
                                property("font-family", "Urbanist, sans-serif")
                            }
                            .toAttrs {
                                attr("placeholder", t.chatCommunityNamePlaceholder)
                                onInput { e -> onGroupNameChanged(e.target.asDynamic().value as String) }
                            }
                    )
                }
            }
        }

        // Action buttons — Cancel (outline) + Create Community (filled), split
        // evenly across the row (Figma 8425:205265).
        Row(modifier = Modifier.fillMaxWidth().styleModifier { property("gap", s(12)) }) {
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("flex", "1")
                        property("height", s(44))
                        property("border-radius", s(100))
                        property("border", "0.3px solid transparent")
                        property("background", FieldBackground)
                        property("box-sizing", "border-box")
                    }
                    .cursor(Cursor.Pointer)
                    .onClick { onCancel() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(t.commonCancel, modifier = Modifier.bodyLMedium().color(com.arena_web.ui.theme.ThemeVars.Text2))
            }

            val canCreate = groupName.isNotBlank() && !isCreating
            Box(
                modifier = Modifier
                    .styleModifier {
                        property("flex", "1")
                        property("height", s(44))
                        property("border-radius", s(100))
                        property("background", "#0061ff")
                        property("opacity", if (groupName.isNotBlank()) "1" else "0.5")
                    }
                    .cursor(if (canCreate) Cursor.Pointer else Cursor.Default)
                    .onClick { if (canCreate) onCreateGroup() },
                contentAlignment = Alignment.Center
            ) {
                SpanText(
                    if (isCreating) t.chatCreating else t.chatCreateCommunity,
                    modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                )
            }
        }
    }
}
